import React from 'react';
import moment from 'moment';
import 'moment/locale/id';
import Flatpickr from 'react-flatpickr';
import AppointmentDialog from './AppointmentDialog';
import HospitalSelector from './shared/HospitalSelector';
import DoctorSelector from './shared/DoctorSelector';
import { searchDoctors } from './ArticleFormNew/api';
import { isoDate } from './shared/chartFormat';
import { statusColor, statusTint, STATUS_LABEL_KEYS } from './shared/appointmentStatus';

// Pipeline order, not enum order: the day reads left to right as an appointment's
// life. All six statuses get a column so every row in the day total is reachable
// (same rule as SUMMARY_STATUSES in shared/appointmentStatus.js).
const BOARD_STATUSES = [0, 5, 1, 3, 4, 2];

const SOURCE_ICONS = {
  web: 'zmdi-globe',
  android: 'zmdi-smartphone-android',
  ios: 'zmdi-apple',
};

// Fixed data-state pairs (design spec) -- like the status colours, these do not
// follow the ten admin themes.
const CHIPS = [
  { key: 'unconfirmed', color: '#B98900', background: '#FFF6DB', icon: 'zmdi-notifications-none' },
  { key: 'insurance', color: '#B7791F', background: '#FDF3E2', icon: 'zmdi-shield-security' },
  { key: 'new', color: '#41924E', background: '#E9F3EB', icon: 'zmdi-account-add' },
];

// Each insight chip doubles as a client-side board filter.
const CHIP_TESTS = {
  unconfirmed: attrs => attrs.status === 0,
  insurance: attrs => attrs.insurance_status === 'pending',
  new: attrs => attrs.old_patient === false,
};

// The current-hour amber shares the unconfirmed/upcoming foreground on purpose.
const NOW_COLOR = '#B98900';

const MICRO_LABEL = 'small text-muted text-uppercase font-weight-bold';

const initialsOf = (name) => (name || '?')
  .split(' ')
  .map(part => part.charAt(0))
  .slice(0, 2)
  .join('')
  .toUpperCase();

export default class AppointmentOpsBoard extends React.Component {
  constructor(props) {
    super(props);
    moment.locale(props.locale === 'id' ? 'id' : 'en');
    this.state = {
      date: isoDate(new Date()),
      loading: true,
      loadingMore: false,
      error: false,
      data: null,
      page: 1,
      hospitals: [],
      hospitalId: '',
      doctor: null,
      searchText: '',
      q: '',
      chip: null,
      selectedId: null,
      menuRowId: null,
      quickView: null,
      moveError: false,
    };
    // Guards page appends against responses from a superseded filter or date.
    this.requestSeq = 0;
  }

  componentDidMount() {
    this.load();
    // A branch admin is already scoped server-side, so the picker never renders
    // and its list is never needed.
    if (!this.props.hospital_id) {
      fetch('/api/search/hospitals')
        .then(response => (response.ok ? response.json() : []))
        .then(hospitals => {
          if (this.unmounted) return;
          this.setState({ hospitals: Array.isArray(hospitals) ? hospitals : [] });
        })
        .catch(() => {});
    }
    document.addEventListener('mousedown', this.handleOutsideMenu);
  }

  componentDidUpdate(prevProps, prevState) {
    const changed = ['date', 'q', 'hospitalId', 'doctor'].some(key => prevState[key] !== this.state[key]);
    if (changed) this.load();
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.searchTimer);
    clearTimeout(this.moveErrorTimer);
    document.removeEventListener('mousedown', this.handleOutsideMenu);
  }

  handleOutsideMenu = (event) => {
    if (this.state.menuRowId && !event.target.closest('.appointment-board__menu-wrap')) {
      this.setState({ menuRowId: null });
    }
  };

  query = () => {
    const params = new URLSearchParams({ date: this.state.date });
    const hospitalId = this.props.hospital_id || this.state.hospitalId;
    if (hospitalId) params.set('hospital_id', hospitalId);
    if (this.state.doctor) params.set('doctor_id', this.state.doctor.id);
    if (this.state.q) params.set('q', this.state.q);
    return params;
  };

  load = (page = 1) => {
    const seq = ++this.requestSeq;
    // Page 1 repaints the board; later pages keep the columns on screen and
    // only disable the load-more button while the append is in flight.
    if (page === 1) this.setState({ loading: true, error: false, page });
    else this.setState({ loadingMore: true, page });

    const params = this.query();
    params.set('per_page', 50);
    params.set('page', page);

    fetch(`/api/appointments/day?${params}`)
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(data => {
        if (this.unmounted || seq !== this.requestSeq) return;
        this.setState(prevState => {
          // Appends keep the latest aggregates but stack the new rows onto the
          // ones already on screen. Rows shifting between fetches (a live insert,
          // or the overflow clamp re-serving the last page) would repeat ids, so
          // already-loaded rows are dropped from the incoming page.
          let next = data;
          if (page > 1 && prevState.data) {
            const seen = new Set(prevState.data.appointments.data.map(row => row.id));
            const fresh = data.appointments.data.filter(row => !seen.has(row.id));
            next = { ...data, appointments: { ...data.appointments, data: prevState.data.appointments.data.concat(fresh) } };
          }
          return { data: next, loading: false, loadingMore: false };
        });
      })
      .catch(() => {
        if (this.unmounted || seq !== this.requestSeq) return;
        // A failed append leaves the loaded board alone; only page 1 blanks
        // into the error message.
        this.setState({ loading: false, loadingMore: false, error: page === 1 });
      });
  };

  handleSearch = (event) => {
    const searchText = event.target.value;
    this.setState({ searchText });
    clearTimeout(this.searchTimer);
    // One request per pause in typing, not one per keystroke.
    this.searchTimer = setTimeout(() => this.setState({ q: searchText }), 300);
  };

  setDate = (date) => this.setState({ date, selectedId: null, menuRowId: null });

  shiftDay = (delta) => this.setDate(moment(this.state.date).add(delta, 'day').format('YYYY-MM-DD'));

  // The counters describe the whole day and the endpoint keeps them stable while the
  // list filters, so they are read straight off the payload.
  countFor = (status) => {
    const counts = (this.state.data && this.state.data.status_counts) || {};
    return counts[String(status)] || 0;
  };

  rows = () => ((this.state.data && this.state.data.appointments && this.state.data.appointments.data) || []);

  boardRows = () => {
    const { chip } = this.state;
    const rows = this.rows();
    return chip ? rows.filter(row => CHIP_TESTS[chip](row.attributes)) : rows;
  };

  // All three chips count the whole filtered day server-side, so they stay
  // right even when only the first page of rows is loaded.
  chipCount = (key) => ((this.state.data && this.state.data.chip_counts) || {})[key] || 0;

  exportCsv = () => {
    const params = this.query();
    params.delete('date');
    params.set('start', this.state.date);
    params.set('end', moment(this.state.date).add(1, 'day').format('YYYY-MM-DD'));
    window.location.href = `/api/appointments/export?${params}`;
  };

  // Same optimistic contract as AppointmentTable's inline editor: move the card
  // first, PUT after, and put it back if the server says no.
  moveTo = (row, status) => {
    const prevData = this.state.data;
    const counts = { ...prevData.status_counts };
    counts[String(row.attributes.status)] = Math.max((counts[String(row.attributes.status)] || 0) - 1, 0);
    counts[String(status)] = (counts[String(status)] || 0) + 1;

    // The unconfirmed insight chip is server-counted now, so a move in or out
    // of status 0 has to adjust it optimistically like status_counts above.
    const chips = { ...prevData.chip_counts };
    if (row.attributes.status === 0) chips.unconfirmed = Math.max((chips.unconfirmed || 0) - 1, 0);
    if (status === 0) chips.unconfirmed = (chips.unconfirmed || 0) + 1;

    this.setState({
      menuRowId: null,
      moveError: false,
      data: {
        ...prevData,
        status_counts: counts,
        chip_counts: chips,
        appointments: {
          ...prevData.appointments,
          data: prevData.appointments.data.map(item => (
            item.id === row.id ? { ...item, attributes: { ...item.attributes, status } } : item
          )),
        },
      },
    });

    fetch(`/api/appointments/${row.attributes.slug}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body: JSON.stringify({
        attributes: { status, queue_number: row.attributes.queue_number || '' },
      }),
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      // The update endpoint answers 200 with success:false on validation
      // failures, so response.ok alone would leave a refused move on screen.
      .then(json => {
        if (!json || json.success === false) throw new Error('update refused');
      })
      .catch(() => {
        if (this.unmounted) return;
        // A snapshot restore could wipe another already-accepted move; asking the
        // server is the only rollback that cannot lie.
        this.setState({ moveError: true });
        this.load();
        clearTimeout(this.moveErrorTimer);
        this.moveErrorTimer = setTimeout(() => {
          if (!this.unmounted) this.setState({ moveError: false });
        }, 6000);
      });
  };

  // Which tone an hour bar takes only means anything on today's board; any other
  // day is entirely behind or ahead of the admin.
  barTone = (hour) => {
    const today = isoDate(new Date());
    if (this.state.date < today) return 'past';
    if (this.state.date > today) return 'future';
    const nowHour = `${String(new Date().getHours()).padStart(2, '0')}:00`;
    if (hour === nowHour) return 'now';
    return hour < nowHour ? 'past' : 'future';
  };

  volume = () => {
    // Server-side hour buckets over the whole filtered day, so the strip stays
    // right even when only the first page of rows is loaded.
    const buckets = (this.state.data && this.state.data.hour_counts) || {};
    // Fixed clinic window; early or late rows extend it rather than vanish.
    const sorted = Object.keys(buckets).sort();
    const hours = sorted.filter(hour => hour < '07:00');
    for (let h = 7; h <= 17; h += 1) hours.push(`${String(h).padStart(2, '0')}:00`);
    sorted.forEach(hour => {
      if (hour > '17:00') hours.push(hour);
    });
    return { hours, buckets };
  };

  renderCommandRow() {
    const { labels } = this.props;
    const { date, searchText, hospitals, hospitalId, doctor, data } = this.state;
    const summary = data && [
      labels.appointments_count.replace('%{count}', data.total || 0),
      labels.hospitals_count.replace('%{count}', data.hospitals_count || 0),
      labels.doctors_count.replace('%{count}', data.doctors_count || 0),
    ].join(' · ');

    return (
      <div className="card mb-3 appointment-board__command">
        <div className="card-body d-flex align-items-center flex-wrap px-3 py-2">
          <button type="button" className="btn btn-light btn-sm mr-1" aria-label={labels.prev_day} onClick={() => this.shiftDay(-1)}>
            <i className="zmdi zmdi-chevron-left" aria-hidden="true" />
          </button>
          <button type="button" className="btn btn-light btn-sm mr-2" aria-label={labels.next_day} onClick={() => this.shiftDay(1)}>
            <i className="zmdi zmdi-chevron-right" aria-hidden="true" />
          </button>

          <Flatpickr
            value={date}
            options={{ dateFormat: 'Y-m-d', disableMobile: true }}
            onChange={dates => { if (dates.length) this.setDate(isoDate(dates[0])); }}
            render={(_props, ref) => (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center mr-2"
                aria-label={labels.pick_date}
                ref={ref}
              >
                <i className="zmdi zmdi-calendar mr-1" aria-hidden="true" />
                {moment(date).format('dddd, D MMMM YYYY')}
                <i className="zmdi zmdi-chevron-down ml-1" aria-hidden="true" />
              </button>
            )}
          />

          <button type="button" className="btn btn-outline-primary btn-sm mr-2" onClick={() => this.setDate(isoDate(new Date()))}>
            {labels.today}
          </button>

          <div className="appointment-board__search form-group position-relative flex-grow-1 mb-0 mr-2">
            <i className="zmdi zmdi-search appointment-board__search-icon" aria-hidden="true" />
            <input
              type="text"
              className="form-control"
              placeholder={labels.day_search_placeholder}
              value={searchText}
              onChange={this.handleSearch}
            />
            <i className="form-group__bar" />
          </div>

          {/* A branch admin is already scoped server-side, so the picker would be a
              control that cannot change anything. */}
          {!this.props.hospital_id && (
            <div className="appointment-board__select mr-2">
              <HospitalSelector
                hospitals={hospitals}
                selectedId={hospitalId}
                onSelect={id => this.setState({ hospitalId: id })}
                onClear={() => this.setState({ hospitalId: '' })}
                translations={labels}
              />
            </div>
          )}

          <div className="appointment-board__select mr-2">
            <DoctorSelector
              variant="dropdown"
              multiple={false}
              selectedDoctors={doctor ? [doctor] : []}
              onSelect={selected => this.setState({ doctor: selected })}
              onRemove={() => this.setState({ doctor: null })}
              searchDoctors={searchDoctors}
              translations={labels}
            />
          </div>

          <div className="d-flex align-items-center ml-auto">
            {summary && <span className="text-muted small d-none d-xl-inline mr-2">{summary}</span>}
            <button type="button" className="btn btn-outline-primary btn-sm mr-1" onClick={this.exportCsv}>
              <i className="zmdi zmdi-download mr-1" aria-hidden="true" />
              {labels.export}
            </button>
            <button type="button" className="btn btn-light btn-sm" aria-label={labels.refresh} onClick={() => this.load()}>
              <i className="zmdi zmdi-refresh" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderProgress() {
    const { labels } = this.props;
    const total = (this.state.data && this.state.data.total) || 0;
    const done = this.countFor(4) + this.countFor(3);
    const pct = total ? Math.round((done / total) * 100) : 0;
    const radius = 26;
    const circumference = 2 * Math.PI * radius;

    return (
      <div className="d-flex align-items-center border-right pr-4 mr-4">
        <svg className="flex-shrink-0 mr-3" viewBox="0 0 72 72" width="72" height="72" role="img" aria-label={`${pct}%`}>
          <circle className="appointment-board__donut-track" cx="36" cy="36" r={radius} />
          <circle
            className="appointment-board__donut-arc"
            cx="36"
            cy="36"
            r={radius}
            strokeDasharray={`${(circumference * pct) / 100} ${circumference}`}
          />
          <text className="appointment-board__donut-text" x="36" y="36" textAnchor="middle" dominantBaseline="central">
            {pct}%
          </text>
        </svg>
        <div>
          <div className={MICRO_LABEL}>{labels.progress_title}</div>
          <div className="font-weight-bold">
            {labels.progress_done.replace('%{done}', done).replace('%{total}', total)}
          </div>
          <div className="small text-muted">
            {labels.progress_checkin.replace('%{count}', this.countFor(3))}
          </div>
        </div>
      </div>
    );
  }

  renderVolume() {
    const { labels } = this.props;
    const { hours, buckets } = this.volume();
    const max = Math.max(1, ...hours.map(hour => buckets[hour] || 0));
    const isToday = this.state.date === isoDate(new Date());

    return (
      <div className="flex-grow-1 border-right pr-4 mr-4">
        <div className="d-flex justify-content-between align-items-baseline mb-1">
          <span className={MICRO_LABEL}>{labels.volume_title}</span>
          {isToday && (
            <span className="small font-weight-bold" style={{ color: NOW_COLOR }}>
              ● {labels.volume_now.replace('%{time}', moment().format('HH:mm'))}
            </span>
          )}
        </div>
        <div className="appointment-board__bars">
          {hours.map(hour => {
            const count = buckets[hour] || 0;
            return (
              <div key={hour} className="appointment-board__bar-slot" title={`${hour} · ${count}`}>
                <div
                  className={`appointment-board__bar appointment-board__bar--${this.barTone(hour)}`}
                  style={{ height: `${count ? Math.max(Math.round((count / max) * 46), 4) : 2}px` }}
                />
              </div>
            );
          })}
        </div>
        <div className="appointment-board__bar-axis" aria-hidden="true">
          {hours.filter((_, index) => index % 2 === 0).map(hour => <span key={hour}>{hour}</span>)}
        </div>
      </div>
    );
  }

  renderChips() {
    const { labels } = this.props;
    const { chip } = this.state;

    return (
      <div className="appointment-board__chips">
        {CHIPS.map(({ key, color, background, icon }) => {
          const active = chip === key;
          return (
            <button
              key={key}
              type="button"
              className={`badge badge-pill appointment-board__chip${active ? ' is-active' : ''}`}
              style={{ color, backgroundColor: background }}
              aria-pressed={active}
              onClick={() => this.setState({ chip: active ? null : key, selectedId: null, menuRowId: null })}
            >
              <i className={`zmdi ${icon} mr-1`} aria-hidden="true" />
              {labels[`insight_${key}`].replace('%{count}', this.chipCount(key))}
            </button>
          );
        })}
      </div>
    );
  }

  renderPulse() {
    return (
      <div className="card mb-3">
        <div className="card-body d-flex align-items-center flex-wrap px-4 py-3">
          {this.renderProgress()}
          {this.renderVolume()}
          {this.renderChips()}
        </div>
      </div>
    );
  }

  renderMenu(row) {
    const { labels } = this.props;
    // Fixed positioning escapes the column card and the horizontal-scroll
    // wrapper, whose overflow would otherwise behead the menu on low cards.
    // Flips upward when the anchor sits too close to the viewport bottom.
    const anchor = this.state.menuAnchor;
    const MENU_HEIGHT = 260;
    const style = anchor ? {
      position: 'fixed',
      left: 'auto',
      right: Math.max(window.innerWidth - anchor.right, 8),
      ...(anchor.bottom + MENU_HEIGHT > window.innerHeight
        ? { top: 'auto', bottom: Math.max(window.innerHeight - anchor.top + 4, 8) }
        : { top: anchor.bottom + 4, bottom: 'auto' }),
    } : undefined;

    return (
      <div className="dropdown-menu dropdown-menu-right show" style={style} onClick={event => event.stopPropagation()}>
        <button
          type="button"
          className="dropdown-item"
          onClick={() => this.setState({ quickView: row, menuRowId: null })}
        >
          <i className="zmdi zmdi-eye mr-2" aria-hidden="true" />
          {labels.view}
        </button>
        <button type="button" className="dropdown-item" onClick={() => Turbolinks.visit(row.attributes.url)}>
          <i className="zmdi zmdi-open-in-new mr-2" aria-hidden="true" />
          {labels.open}
        </button>
        <div className="dropdown-header">{labels.move_to}</div>
        {BOARD_STATUSES.filter(status => status !== row.attributes.status).map(status => (
          <button
            key={status}
            type="button"
            className="dropdown-item d-flex align-items-center"
            onClick={() => this.moveTo(row, status)}
          >
            <span className="appointment-board__menu-dot mr-2" style={{ backgroundColor: statusColor(status) }} />
            {labels[STATUS_LABEL_KEYS[status]]}
          </button>
        ))}
      </div>
    );
  }

  renderKebab(row, extraClass) {
    const { labels } = this.props;
    const open = this.state.menuRowId === row.id;

    // __menu-wrap has no styles of its own; it is the outside-click hook.
    return (
      <span className={`dropdown appointment-board__menu-wrap${extraClass ? ` ${extraClass}` : ''}`}>
        <button
          type="button"
          className="btn btn-sm p-0 border-0 text-muted"
          aria-label={labels.move_to}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={event => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            this.setState({
              menuRowId: open ? null : row.id,
              menuAnchor: { top: rect.top, right: rect.right, bottom: rect.bottom },
            });
          }}
        >
          <i className="zmdi zmdi-more-vert" aria-hidden="true" />
        </button>
        {open && this.renderMenu(row)}
      </span>
    );
  }

  renderBadges(attrs) {
    const { labels } = this.props;
    const badges = [];
    if (attrs.old_patient === false) badges.push({ key: 'new', text: labels.badge_new, color: '#41924E', background: '#E9F3EB' });
    if (attrs.insurance_status === 'pending') badges.push({ key: 'ins', text: labels.badge_ins_pending, color: '#B7791F', background: '#FDF3E2' });
    if (attrs.insurance_status === 'rejected') badges.push({ key: 'ins', text: labels.badge_ins_rejected, color: '#D14B48', background: '#FFECEB' });

    return badges.map(({ key, text, color, background }) => (
      <span key={key} className="badge badge-pill mr-1" style={{ color, backgroundColor: background }}>{text}</span>
    ));
  }

  renderCard(row) {
    const { labels } = this.props;
    const attrs = row.attributes;
    const selected = this.state.selectedId === row.id;
    const estimation = attrs.estimation_time ? attrs.estimation_time.slice(0, 5) : null;

    return (
      <article
        key={row.id}
        className={`appointment-board__card p-2 mb-2${selected ? ' is-selected' : ''}`}
        onClick={() => this.setState({ selectedId: selected ? null : row.id })}
      >
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="appointment-board__mono small font-weight-bold text-truncate mr-1">{attrs.time_label || '--:--'}</span>
          {attrs.queue_number
            ? <span className="badge appointment-board__queue flex-shrink-0 ml-1">{attrs.queue_number}</span>
            : this.renderKebab(row)}
        </div>

        <div className="d-flex align-items-center mb-1">
          <span className="appointment-board__avatar mr-2" aria-hidden="true">{initialsOf(attrs.name)}</span>
          <span className="d-flex flex-column">
            <a className="appointment-board__name small font-weight-bold" href={attrs.url} onClick={event => event.stopPropagation()}>
              {attrs.name}
            </a>
            <small className="text-muted">
              {attrs.medical_record_number ? `${labels.mrn} ${attrs.medical_record_number}` : labels.no_mrn}
            </small>
          </span>
        </div>

        <div className="d-flex flex-column small text-muted mb-1">
          <span>{attrs.doctor_name}</span>
          {attrs.selected_unit_name && <span>{attrs.selected_unit_name}</span>}
        </div>

        {attrs.status === 3 && estimation && (
          <div className="small text-muted mb-1">{labels.note_estimation.replace('%{time}', estimation)}</div>
        )}

        <div className="d-flex align-items-center">
          <span className="d-flex flex-wrap align-items-center mr-1">{this.renderBadges(attrs)}</span>
          {SOURCE_ICONS[attrs.source] && (
            <i className={`zmdi ${SOURCE_ICONS[attrs.source]} small text-muted ml-auto align-self-center flex-shrink-0`} aria-hidden="true" />
          )}
        </div>

        {selected && (
          <div className="d-flex align-items-center pt-1">
            {/* btn--icon-text opts out of custom.scss's :has(zmdi-eye:only-child)
                rule that squashes table action buttons into 27px icon circles. */}
            <button
              type="button"
              className="btn btn-primary btn-sm btn--icon-text"
              onClick={event => {
                event.stopPropagation();
                this.setState({ quickView: row });
              }}
            >
              <i className="zmdi zmdi-eye mr-1" aria-hidden="true" />
              {labels.view}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm ml-1"
              onClick={event => {
                event.stopPropagation();
                Turbolinks.visit(attrs.url);
              }}
            >
              <i className="zmdi zmdi-open-in-new mr-1" aria-hidden="true" />
              {labels.open}
            </button>
            {this.renderKebab(row, 'ml-auto')}
          </div>
        )}
      </article>
    );
  }

  renderSkeletonCard(key) {
    return (
      <div className="appointment-board__card p-2 mb-2" key={key}>
        <div className="appointment-board__skeleton-line w-25 mb-2" />
        <div className="d-flex align-items-center">
          <span className="appointment-board__skeleton-avatar mr-2" />
          <div className="flex-grow-1">
            <div className="appointment-board__skeleton-line w-75 mb-1" />
            <div className="appointment-board__skeleton-line w-50" />
          </div>
        </div>
      </div>
    );
  }

  renderSkeleton() {
    const { labels } = this.props;

    return (
      <React.Fragment>
        {/* The pulse strip renders its real (stale) self on a day change; this
            placeholder only stands in before the first payload. */}
        {!this.state.data && (
          <div className="card mb-3">
            <div className="card-body">
              <div className="appointment-board__skeleton-line w-25 mb-2" />
              <div className="appointment-board__skeleton-line w-50 mb-2" />
              <div className="appointment-board__skeleton-line w-75" />
            </div>
          </div>
        )}
        <div className="overflow-auto pb-1">
          <div className="appointment-board__columns">
            {BOARD_STATUSES.map(status => (
              <section className="card appointment-board__column" key={status}>
                <header
                  className="appointment-board__column-head px-3 py-2 small font-weight-bold"
                  style={{ backgroundColor: statusTint(status), color: statusColor(status) }}
                >
                  {labels[STATUS_LABEL_KEYS[status]]}
                </header>
                <div className="p-2">
                  {[0, 1].map(key => this.renderSkeletonCard(key))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderBoard() {
    const { labels } = this.props;
    const { loading, loadingMore, error, data } = this.state;

    if (loading) return this.renderSkeleton();
    if (error) return <p className="text-center text-danger py-4">{labels.load_error}</p>;
    if (!data || !data.total) return <p className="text-center text-muted py-4">{labels.no_appointments_day}</p>;

    const byStatus = {};
    this.boardRows().forEach(row => {
      (byStatus[row.attributes.status] = byStatus[row.attributes.status] || []).push(row);
    });

    const pagination = data.pagination;
    const loaded = this.rows().length;

    return (
      <React.Fragment>
        <div className="overflow-auto pb-1">
          <div className="appointment-board__columns">
            {BOARD_STATUSES.map(status => (
              <section className="card appointment-board__column" key={status}>
                <header
                  className="appointment-board__column-head d-flex justify-content-between align-items-center px-3 py-2 small font-weight-bold"
                  style={{ backgroundColor: statusTint(status), color: statusColor(status) }}
                >
                  <span>{labels[STATUS_LABEL_KEYS[status]]}</span>
                  <span className="appointment-board__mono">{this.countFor(status)}</span>
                </header>
                <div className="p-2">
                  {(byStatus[status] || []).map(row => this.renderCard(row))}
                </div>
              </section>
            ))}
          </div>
        </div>
        {pagination && loaded < pagination.count && (
          <div className="text-center py-3">
            <span className="text-muted small mr-2">
              {labels.showing_of.replace('%{shown}', loaded).replace('%{total}', pagination.count)}
            </span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={loadingMore}
              onClick={() => this.load(this.state.page + 1)}
            >
              {labels.load_more}
            </button>
          </div>
        )}
      </React.Fragment>
    );
  }

  render() {
    const { labels } = this.props;
    const { data, quickView, moveError } = this.state;

    return (
      <div className="appointment-board">
        {this.renderCommandRow()}
        {data && this.renderPulse()}

        {moveError && (
          <div className="alert alert-danger alert-dismissible mb-3" role="alert">
            {labels.move_error}
            <button type="button" className="close" aria-label={labels.close} onClick={() => this.setState({ moveError: false })}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}

        {this.renderBoard()}

        <AppointmentDialog
          open={Boolean(quickView)}
          onClose={() => this.setState({ quickView: null })}
          appointment={quickView}
          labels={labels}
        />
      </div>
    );
  }
}
